import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PosAPI from "../../api/inventory/posApi";
import TableShimmer from "../../components/partials/TableShimmer";
import Pagination from "../../components/partials/Pagination";
import RowsPerPage from "../../components/partials/RowsPerPage";
import useQueryFilters from "../../hooks/useQueryFilters";
import { showError, showSuccess } from "../../utils/toastUtils";
import { inr } from "../../utils/posBilling";
import PaymentModal, { methodLabel } from "../../components/PaymentModal";

const fmtDate = (d) =>
  new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

const PAY_BADGE = { paid: "bg-success", partial: "bg-warning", unpaid: "bg-danger" };
const errMsg = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

function OfflineSales() {
  const { query, updateFilter, handleSearch, searchParams, searchMessage } = useQueryFilters();

  const [loading, setLoading] = useState(false);
  const [sales, setSales] = useState([]);
  const [pagination, setPagination] = useState({});
  const [summary, setSummary] = useState(null);

  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const [collectTarget, setCollectTarget] = useState(null);
  const [collecting, setCollecting] = useState(false);

  const from = searchParams.get("from") || "";
  const to = searchParams.get("to") || "";

  const fetchSales = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, sumRes] = await Promise.all([
        PosAPI.listSales(searchParams),
        PosAPI.summary({ from: from || undefined, to: to || undefined }),
      ]);
      if (listRes.code === 200) {
        setSales(listRes.data.sales);
        setPagination(listRes.data.pagination);
      }
      if (sumRes.code === 200) setSummary(sumRes.data);
    } catch (err) {
      showError("Failed to load offline sales");
    } finally {
      setLoading(false);
    }
  }, [searchParams, from, to]);

  useEffect(() => {
    fetchSales();
  }, [fetchSales]);

  const confirmCancel = async () => {
    if (cancelReason.trim().length < 3) return showError("Please enter a reason");
    setCancelling(true);
    try {
      const res = await PosAPI.cancelSale(cancelTarget.id, cancelReason.trim());
      if (res.code === 200) {
        showSuccess(res.message);
        setCancelTarget(null);
        setCancelReason("");
        fetchSales();
      } else showError(res.message);
    } catch (err) {
      showError(errMsg(err, "Cancel failed"));
    } finally {
      setCancelling(false);
    }
  };

  const collectDue = async (payment) => {
    setCollecting(true);
    try {
      const res = await PosAPI.addPayment(collectTarget.id, payment);
      if (res.code === 200) {
        showSuccess(res.message);
        setCollectTarget(null);
        fetchSales();
      } else showError(res.message);
    } catch (err) {
      showError(errMsg(err, "Could not record payment"));
    } finally {
      setCollecting(false);
    }
  };

  const cards = summary
    ? [
        { label: "Invoices", value: summary.invoices, icon: "ti ti-file-invoice", cls: "bg-primary" },
        { label: "Sales", value: inr(summary.revenue), icon: "ti ti-currency-rupee", cls: "bg-success" },
        { label: "Collected", value: inr(summary.collected), icon: "ti ti-wallet", cls: "bg-info" },
        { label: "Due", value: inr(summary.due), icon: "ti ti-alert-circle", cls: "bg-danger" },
        { label: "GST Collected", value: inr(summary.tax), icon: "ti ti-receipt-tax", cls: "bg-secondary" },
        { label: "Items Sold", value: summary.itemsSold, icon: "ti ti-package", cls: "bg-warning" },
      ]
    : [];

  return (
    <>
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <h4>Offline Sales</h4>
            <h6>All invoices generated from POS {!from && !to && "· Summary shows today"}</h6>
          </div>
          <div className="page-btn">
            <Link to="/pos" className="btn btn-primary">
              <i className="ti ti-device-desktop me-1" /> Open POS
            </Link>
          </div>
        </div>

        <div className="row g-3 mb-2">
          {cards.map((c) => (
            <div className="col-xl-2 col-md-4 col-6" key={c.label}>
              <div className="card mb-0">
                <div className="card-body d-flex align-items-center gap-2 p-3">
                  <span className={`avatar avatar-md ${c.cls} text-white rounded`}>
                    <i className={`${c.icon} fs-18`} />
                  </span>
                  <div className="overflow-hidden">
                    <small className="text-muted d-block">{c.label}</small>
                    <b className="text-truncate d-block">{c.value}</b>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {summary && (
          <div className="d-flex flex-wrap gap-2 mb-3 small">
            <span className="text-muted">Collection by method:</span>
            {Object.entries(summary.byMethod || {})
              .filter(([, v]) => v > 0)
              .map(([m, v]) => (
                <span key={m} className="badge bg-light text-dark border">
                  {methodLabel(m)}: {inr(v)}
                </span>
              ))}
            {!summary.collected && <span className="text-muted">No payments yet</span>}
            {summary.cancelled > 0 && <span className="badge bg-danger-subtle text-danger">{summary.cancelled} cancelled</span>}
          </div>
        )}

        <div className="card">
          <div className="card-header">
            <div className="row g-2 align-items-end">
              <div className="col-md-3">
                <div className="input-group">
                  <span className="input-group-text"><i className="ti ti-search" /></span>
                  <input
                    className="form-control"
                    placeholder="Invoice no, customer or mobile"
                    defaultValue={query.search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                </div>
                {searchMessage && <small className="text-danger">{searchMessage}</small>}
              </div>
              <div className="col-md-2 col-6">
                <label className="form-label small mb-0">From</label>
                <input type="date" className="form-control" value={from} onChange={(e) => updateFilter("from", e.target.value)} />
              </div>
              <div className="col-md-2 col-6">
                <label className="form-label small mb-0">To</label>
                <input type="date" className="form-control" value={to} onChange={(e) => updateFilter("to", e.target.value)} />
              </div>
              <div className="col-md-2 col-6">
                <select className="form-select" value={searchParams.get("paymentStatus") || ""} onChange={(e) => updateFilter("paymentStatus", e.target.value)}>
                  <option value="">All payments</option>
                  <option value="paid">Paid</option>
                  <option value="partial">Partially paid</option>
                  <option value="unpaid">Unpaid</option>
                </select>
              </div>
              <div className="col-md-2 col-6">
                <select className="form-select" value={searchParams.get("status") || ""} onChange={(e) => updateFilter("status", e.target.value)}>
                  <option value="">All invoices</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="col-md-1 col-6">
                {(from || to) && (
                  <button
                    className="btn btn-outline-secondary w-100"
                    title="Clear dates"
                    onClick={() => {
                      updateFilter("from", "");
                      updateFilter("to", "");
                    }}
                  >
                    <i className="ti ti-x" />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table w-100 mb-0">
                <thead className="thead-light">
                  <tr>
                    <th>Invoice No.</th>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Items</th>
                    <th className="text-end">Amount</th>
                    <th>Payment</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <TableShimmer rows={10} cols={8} />
                  ) : sales.length ? (
                    sales.map((s) => (
                      <tr key={s.id}>
                        <td>
                          <Link to={`/pos/offline-sales/${s.id}`} className="fw-semibold">
                            {s.invoiceNo}
                          </Link>
                        </td>
                        <td className="text-nowrap">{fmtDate(s.createdAt)}</td>
                        <td>
                          {s.customer?.name || "Walk-in Customer"}
                          {s.customerId && <i className="ti ti-user-check text-success ms-1" title="Registered customer" />}
                          {s.customer?.phone && <small className="d-block text-muted">{s.customer.phone}</small>}
                        </td>
                        <td>
                          <span className="text-truncate d-inline-block" style={{ maxWidth: 180 }} title={s.firstItem}>
                            {s.firstItem}
                          </span>
                          {s.itemCount > 1 && <small className="text-muted"> +{s.itemCount - 1} more</small>}
                          <small className="d-block text-muted">Qty {s.totalQty}</small>
                        </td>
                        <td className="text-end fw-semibold">
                          {inr(s.grandTotal)}
                          <small className="d-block text-muted fw-normal">GST {inr(s.totalTax)}</small>
                        </td>
                        <td>
                          <span className={`badge ${PAY_BADGE[s.paymentStatus] || "bg-secondary"} text-capitalize`}>{s.paymentStatus}</span>
                          {s.paymentMethods.length > 0 && (
                            <small className="d-block text-muted">{s.paymentMethods.map(methodLabel).join(" + ")}</small>
                          )}
                          {s.dueAmount > 0 && <small className="d-block text-danger">Due {inr(s.dueAmount)}</small>}
                        </td>
                        <td>
                          <span className={`badge ${s.status === "completed" ? "bg-success-subtle text-success" : "bg-danger"} text-capitalize`}>
                            {s.status}
                          </span>
                        </td>
                        <td className="action-table-data">
                          <div className="edit-delete-action">
                            <Link to={`/pos/offline-sales/${s.id}`} className="me-2 p-2" title="View / Print">
                              <i className="ti ti-printer" />
                            </Link>
                            {s.status === "completed" && s.dueAmount > 0 && (
                              <span className="me-2 p-2 box" title="Collect payment" role="button" onClick={() => setCollectTarget(s)}>
                                <i className="ti ti-cash" />
                              </span>
                            )}
                            {s.status === "completed" && (
                              <span className="p-2 box" title="Cancel invoice" role="button" onClick={() => setCancelTarget(s)}>
                                <i className="ti ti-ban" />
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="text-center p-4 text-muted">
                        No offline sales found. <Link to="/pos">Open POS</Link> to create one.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="row">
              <RowsPerPage value={searchParams.get("limit") || "10"} onChange={(val) => updateFilter("limit", val)} />
              <Pagination pagination={pagination} updateFilter={updateFilter} />
            </div>
          </div>
        </div>
      </div>

      {/* ---------- CANCEL ---------- */}
      {cancelTarget && (
        <>
          <div className="modal fade show d-block" tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Cancel {cancelTarget.invoiceNo}?</h5>
                  <button className="btn-close" onClick={() => setCancelTarget(null)} disabled={cancelling} />
                </div>
                <div className="modal-body">
                  <p className="text-muted small mb-2">
                    The invoice will be marked as cancelled and the stock for all items will be added back. This cannot be undone.
                  </p>
                  {cancelTarget.paidAmount > 0 && (
                    <div className="alert alert-warning small py-2">
                      {inr(cancelTarget.paidAmount)} was received on this invoice. Refund it to the customer manually.
                    </div>
                  )}
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Reason (e.g. customer returned the items)"
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                  />
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setCancelTarget(null)} disabled={cancelling}>
                    Close
                  </button>
                  <button className="btn btn-danger" onClick={confirmCancel} disabled={cancelling}>
                    {cancelling ? "Cancelling..." : "Cancel Invoice"}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}

      {/* ---------- COLLECT DUE ---------- */}
      {collectTarget && (
        <PaymentModal
          total={collectTarget.dueAmount}
          title={`Collect payment · ${collectTarget.invoiceNo}`}
          confirmLabel="Record Payment"
          allowPayLater={false}
          submitting={collecting}
          onClose={() => setCollectTarget(null)}
          onConfirm={collectDue}
        />
      )}
    </>
  );
}

export default OfflineSales;